/**
 * API Configuration
 * Centralized API URL and helper functions
 *
 * BUILD VERSION: 2025-01-15-v4 - Production Release
 * All debug logging removed for security
 */

// API base URL.
// - Dev: backend runs on :3001. We target the SAME hostname the page is on
//   (not a hardcoded localhost) so the API request carries a tenant-matching
//   Host header — e.g. valleyrp.localhost:5173 → valleyrp.localhost:3001, which
//   the backend resolves to the Valley tenant (mwrp.localhost/localhost → MWRP).
//   Without this, all dev API calls hit localhost → always MWRP.
// - Prod: backend is reverse-proxied to /api/* on the SAME origin as the
//   frontend (mwrp.gg, valleyrp.gg), so we use a relative base.
// Set VITE_DEV_SAME_ORIGIN=1 to use the Vite /api proxy instead (see vite.config.js).
export const API_URL = import.meta.env.DEV && !import.meta.env.VITE_DEV_SAME_ORIGIN ? `http://${window.location.hostname}:3001` : ''

/**
 * Get full API endpoint URL
 * @param {string} endpoint - API endpoint (e.g., '/api/auth/verify')
 * @returns {string} Full API URL
 */
export const getApiUrl = (endpoint) => {
  // Remove leading slash if present to avoid double slashes
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
  
  // Ensure API_URL doesn't end with a slash
  const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL
  
  return `${baseUrl}${cleanEndpoint}`
}

/**
 * Make authenticated API request
 * @param {string} endpoint - API endpoint
 * @param {object} options - Fetch options
 * @returns {Promise<Response>}
 */
export const apiRequest = async (endpoint, options = {}) => {
  const token = localStorage.getItem('discord_token')

  // Check if body is FormData
  const isFormData = options.body instanceof FormData

  const headers = {
    ...options.headers,
  }

  // Only set Content-Type for non-FormData requests
  // For FormData, let the browser set it with the correct boundary
  if (!isFormData && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json'
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const fullUrl = getApiUrl(endpoint)
  try {
    const response = await fetch(fullUrl, {
      ...options,
      headers,
    })

    // Handle CORS or network errors
    if (!response.ok && response.status === 0) {
      throw new Error('Network error or CORS issue. Check API URL and HTTPS configuration.')
    }

    return response
  } catch (error) {
    console.error('API request failed:', error)
    throw error
  }
}

/**
 * API Endpoints
 */
export const API_ENDPOINTS = {
  HEALTH: '/api/health',
  AUTH_CALLBACK: '/api/auth/callback',
  AUTH_VERIFY: '/api/auth/verify',
  STAFF_ROSTER: '/api/staff/roster',
  STAFF_SITS: (discordId) => `/api/staff/${discordId}/sits`,
  STAFF_NOTES_STRIKES: (discordId) => `/api/staff/${discordId}/notes-strikes`,
  STAFF_JOINED_DATE: (discordId) => `/api/staff/${discordId}/joined-date`,
  STAFF_MOVEMENTS: '/api/staff/movements',
  STAFF_SYNC: '/api/staff/sync',
  STAFF_HOURS: '/api/staff/hours',
  STAFF_HOURS_DETAIL: (discordId) => `/api/staff/${discordId}/hours`,
  BUSINESS_HOURS: '/api/business-hours',
  BUSINESS_HOURS_DETAIL: (job) => `/api/business-hours/${encodeURIComponent(job)}`,
  GANG_COOLDOWNS: '/api/gang-cooldowns',
  GANG_COOLDOWN: (identifier) => `/api/gang-cooldowns/${encodeURIComponent(identifier)}`,
  APARTMENTS: '/api/apartments',
  APARTMENT: (id) => `/api/apartments/${encodeURIComponent(id)}`,
  APARTMENT_ACTIONS: (id) => `/api/apartments/${encodeURIComponent(id)}/actions`,
  APARTMENT_ACTION_STATUS: (actionId) => `/api/apartments/actions/${encodeURIComponent(actionId)}`,
  APARTMENT_SECURITY: '/api/apartments/security',
  APARTMENT_CHARACTER: (identifier) => `/api/apartments/character/${encodeURIComponent(identifier)}`,
  STAFF_LOA: '/api/loa',
  STAFF_LOA_COUNT: '/api/loa/count',
  STAFF_LOA_REPORT: '/api/loa/report',
  STAFF_LOA_REMOVE: (discordId) => `/api/loa/${discordId}`,
  GANGS: '/api/gangs',
  GANG_STATS: '/api/gangs/stats',
  GANG_BY_ID: (id) => `/api/gangs/${id}`,
  GANG_RESOLVE_OWNER: '/api/gangs/resolve-owner',
  GANG_LINK: (id) => `/api/gangs/${id}/link`,
  GANG_OPERATION: (requestId) => `/api/gang-operations/${encodeURIComponent(requestId)}`,
  VOTE_EXCLUSIONS: '/api/vote-exclusions',
  VOTE_EXCLUSION_BY_ID: (discordId) => `/api/vote-exclusions/${discordId}`,
  VOTE_ELIGIBLE: '/api/vote-eligible',
  VOTES: '/api/votes',
  VOTES_ACTIVE: '/api/votes/active',
  VOTE_BY_ID: (id) => `/api/votes/${id}`,
  VOTE_RESULTS: (id) => `/api/votes/${id}/results`,
  VOTE_RESPONSES: '/api/vote-responses',
  VOTE_HAS_VOTED: (voteId) => `/api/votes/${voteId}/has-voted`,
  PREMIUM_DRUGS: '/api/premium-drugs',
  MEDIA_ASSETS: '/api/media-assets',
  MEDIA_ASSETS_UPLOAD: '/api/media-assets/upload',
  MEDIA_ASSETS_DELETE: '/api/media-assets',
  PREMIUM_DRUG_BY_ID: (id) => `/api/premium-drugs/${id}`,
  DOCUMENTATION: '/api/documentation',
  DOCUMENTATION_BY_ID: (id) => `/api/documentation/${id}`,
  DOCUMENTATION_REORDER: '/api/documentation/reorder',
  REALTOR_LOGS: '/api/realtor-logs',
  REALTOR_LOG_BY_ID: (id) => `/api/realtor-logs/${id}`,

  // Reports (player-submitted in-game reports — any staff)
  REPORTS: '/api/reports',
  REPORT: (id) => `/api/reports/${id}`,
  REPORT_ACTION: (id) => `/api/reports/${id}/action`,

  // Public content (CMS-lite — Phase 4)
  CONTENT_LIST: '/api/content',
  CONTENT_BY_PAGE: (page) => `/api/content/${page}`,

  // Public marketing stats (Discord member count, daily players, uptime)
  SERVER_STATS: '/api/server/stats',

  // Import Logs
  IMPORT_LOGS: '/api/import-logs',
  IMPORT_LOG_BY_ID: (id) => `/api/import-logs/${id}`,

  // Radio Locks
  RADIO_LOCKS: '/api/radio-locks',
  RADIO_LOCK_BY_ID: (id) => `/api/radio-locks/${id}`,

  // Bans Manager (Head Admin+ — search + lift bans in the FiveM bans table)
  BANS_SEARCH: '/api/bans/search',
  BAN_EXPIRE: (id) => `/api/bans/${id}/expire`,
  BAN_BY_ID: (id) => `/api/bans/${id}`,

  // Punishment History lookup (any staff — read-only query of the game DB
  // punishment_history table the in-game admin menu writes on each punish).
  PUNISHMENT_HISTORY: '/api/punishment-history',

  // Tebex transactions (Manager+ — money-free proxy of the Tebex Plugin API)
  TEBEX_TRANSACTIONS: '/api/tebex/transactions',
  TEBEX_TRANSACTION_BY_ID: (txnId) => `/api/tebex/transactions/${txnId}`,
  TEBEX_TRANSACTIONS_BY_PLAYER: (playerId) => `/api/tebex/transactions/by-player/${playerId}`,

  // Server Status Endpoints
  SERVER_PLAYERS: '/api/server/players',
  STAFF_ON_DUTY: '/api/staff/on-duty',

  // Awards API Endpoints
  AWARD_EVENTS: '/api/awards/events',
  AWARD_EVENTS_ACTIVE: '/api/awards/events/active',
  AWARD_EVENT_BY_ID: (id) => `/api/awards/events/${id}`,
  AWARD_EVENT_STATUS: (id) => `/api/awards/events/${id}/status`,
  AWARD_CATEGORIES: (eventId) => `/api/awards/events/${eventId}/categories`,
  AWARD_CATEGORY_BY_ID: (id) => `/api/awards/categories/${id}`,
  AWARD_NOMINATIONS: (categoryId) => `/api/awards/categories/${categoryId}/nominations`,
  AWARD_MY_NOMINATION: (categoryId) => `/api/awards/categories/${categoryId}/my-nomination`,
  AWARD_FINALISTS: (categoryId) => `/api/awards/categories/${categoryId}/finalists`,
  AWARD_VOTE: (categoryId) => `/api/awards/categories/${categoryId}/vote`,
  AWARD_MY_VOTE: (categoryId) => `/api/awards/categories/${categoryId}/my-vote`,
  AWARD_RESULTS: (categoryId) => `/api/awards/categories/${categoryId}/results`,
  AWARD_VOTING_STATUS: (eventId) => `/api/awards/events/${eventId}/voting-status`,
  AWARD_WINNERS: (eventId) => `/api/awards/events/${eventId}/winners`,
  AWARD_CALCULATE_WINNERS: (eventId) => `/api/awards/events/${eventId}/calculate-winners`,
  AWARD_OVERRIDE_WINNER: (categoryId) => `/api/awards/categories/${categoryId}/winner`,

  // Player Lookup (Manager+ — read-only character inspector; name edit is Manager+ write)
  PLAYER_LOOKUP_SEARCH: '/api/player-lookup/search',
  PLAYER_LOOKUP_CHARACTER: (identifier) => `/api/player-lookup/character/${encodeURIComponent(identifier)}`,
  PLAYER_LOOKUP_CHARACTER_NAME: (identifier) => `/api/player-lookup/character/${encodeURIComponent(identifier)}/name`,
  PLAYER_LOOKUP_INVENTORY_WIPE: (identifier) => `/api/player-lookup/character/${encodeURIComponent(identifier)}/inventory/wipe`,
  PLAYER_LOOKUP_VEHICLE_PLATE: (identifier, plate) => `/api/player-lookup/character/${encodeURIComponent(identifier)}/vehicle/${encodeURIComponent(plate)}/plate`,
  PLAYER_LOOKUP_VEHICLE_STASH: (identifier, plate) => `/api/player-lookup/character/${encodeURIComponent(identifier)}/vehicle/${encodeURIComponent(plate)}/stash`,
  PLAYER_LOOKUP_WEED: (identifier) => `/api/player-lookup/character/${encodeURIComponent(identifier)}/weed`,
  PLAYER_LOOKUP_WEED_PROGRESS: (identifier) => `/api/player-lookup/character/${encodeURIComponent(identifier)}/weed/progress`,

  // Player Reimbursement (Manager+ ? inventory / stash / vehicle restoration tooling)
  PLAYER_REIMBURSEMENT_CHARACTER: (identifier) =>
    `/api/player-reimbursement/character/${encodeURIComponent(identifier)}`,

  PLAYER_FILE: (key) => `/api/players/${encodeURIComponent(key)}`,
  PLAYER_FILE_NOTES: (key) => `/api/players/${encodeURIComponent(key)}/notes`,
  PLAYER_FILE_NOTE: (key, id) => `/api/players/${encodeURIComponent(key)}/notes/${encodeURIComponent(id)}`,
  ACTIVITY: '/api/activity',

  // Resource Swiper (temporary tool)
  SWIPER_DECK: '/api/swiper/deck',
  SWIPER_SWIPE: '/api/swiper/swipe',
  SWIPER_ROSTER: '/api/swiper/roster',
  SWIPER_HEARTBEAT: '/api/swiper/heartbeat',
  SWIPER_SUMMARY: '/api/swiper/summary',
  SWIPER_NOTE: '/api/swiper/note',
  SWIPER_UNDO: '/api/swiper/undo',
}

